import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RegionService } from 'src/app/services/region.service';
import { DivisionService } from 'src/app/services/division.service';
import { AdministrativeMappingService } from 'src/app/services/administrative-mapping.service';
import { VehicleService } from 'src/app/services/vehicle.service';
import { PaccsbookingService } from 'src/app/services/paccsbooking.service';
import { DepartmentType } from 'src/enum/departmenttype';
import { LookUpResponse } from 'src/app/models/lookup';
import { ShowDailyReportComponent } from '../../reports/showdailyreport/showdailyreport.component';
import { LotteryService } from 'src/app/services/lottery.service';
import * as moment from 'moment';

@Component({
  selector: 'app-manufacturerreport',
  templateUrl: './manufacturerreport.component.html',
  styleUrls: ['./manufacturerreport.component.scss']
})
export class ManufacturerReportComponent implements OnInit {

  form: FormGroup;

  // Lottery was first run in FY 2026-27 (start year 2026); earlier years have no lottery data.
  private readonly firstLotteryStartYear = 2026;

  monthList: LookUpResponse[] = [
    { key: 1, value: 'January' }, { key: 2, value: 'February' }, { key: 3, value: 'March' },
    { key: 4, value: 'April' }, { key: 5, value: 'May' }, { key: 6, value: 'June' },
    { key: 7, value: 'July' }, { key: 8, value: 'August' }, { key: 9, value: 'September' },
    { key: 10, value: 'October' }, { key: 11, value: 'November' }, { key: 12, value: 'December' }
  ];
  filterMonthList: LookUpResponse[] = this.monthList.slice();

  calendarStartAt: Date = new Date();
  calendarMin: Date | null = null;
  calendarMax: Date | null = null;
  lotteryRunDates: string[] = [];
  public lotteryDateFilter = (d: Date | null): boolean => {
    return this.lotteryRunDates.indexOf(this.formatLotteryDate(d)) >= 0;
  };

  financialYearList: LookUpResponse[] = [];
  filterFinancialYearList: LookUpResponse[] = [];

  // Region -> Division -> Sub Division -> Village, cascading exactly like the Run Lottery
  // screen's own geography filters (AdministrativeMappingService + DivisionService), but every
  // level here stays optional ("-- All ... --") since this page reports at whatever geographic
  // granularity the user picks, rather than requiring one exact village to run a draw against.
  regionList: LookUpResponse[] = [];
  filterRegionList: LookUpResponse[] = [];

  divisionList: LookUpResponse[] = [];
  filterDivisionList: LookUpResponse[] = [];

  subDivisionList: LookUpResponse[] = [];
  filterSubDivisionList: LookUpResponse[] = [];

  villageList: LookUpResponse[] = [];
  filterVillageList: LookUpResponse[] = [];

  departmentList: LookUpResponse[] = [];
  filterDepartmentList: LookUpResponse[] = [];

  goList: LookUpResponse[] = [];
  filterGoList: LookUpResponse[] = [];

  schemeList: LookUpResponse[] = [];
  filterSchemeList: LookUpResponse[] = [];

  componentList: LookUpResponse[] = [];
  filterComponentList: LookUpResponse[] = [];

  productList: LookUpResponse[] = [];
  filterProductList: LookUpResponse[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private regionService: RegionService,
    private divisionService: DivisionService,
    private administrativeMappingService: AdministrativeMappingService,
    private vehicleService: VehicleService,
    private paccsbookingService: PaccsbookingService,
    private lotteryService: LotteryService,
    public tripDialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getFinancialYear(true);
  }

  initializeForm(): void {
    this.form = this.formBuilder.group({
      financialYearId: [null, Validators.required],
      monthId: [0],
      lotteryDate: [null],
      regionId: [0],
      divisionId: [0],
      subDivisionId: [0],
      villageId: [0],
      departmentId: [null, Validators.required],
      goId: [0],
      schemeId: [0],
      componentId: [0],
      productId: [0]
    });
  }

  getFinancialYear(refresh: boolean): void {
    this.regionService.getFinYear(refresh).subscribe(result => {
      if (!result) {
        return;
      }
      this.financialYearList = result.filter((x: any) => Number(x.startYear) >= this.firstLotteryStartYear);
      this.filterFinancialYearList = this.financialYearList.slice();

      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;

      let selectedYear: any;
      if (currentMonth >= 4) {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear && Number(x.endYear) === currentYear + 1);
      } else {
        selectedYear = this.financialYearList.find((x: any) =>
          Number(x.startYear) === currentYear - 1 && Number(x.endYear) === currentYear);
      }
      if (!selectedYear && this.financialYearList.length > 0) {
        selectedYear = this.financialYearList[this.financialYearList.length - 1];
      }
      if (selectedYear) {
        this.form.controls['financialYearId'].setValue(selectedYear.key);
      }
      this.getDepartments(true);
      this.regionLookup();
    });
  }

  onFinancialYearChange(): void {
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.form.controls['departmentId'].setValue(null);
    this.getDepartments(true);

    this.resetGeoFrom('regionId');
    this.regionLookup();
    this.updateCalendarStartAt();
    this.loadLotteryRunDates();
  }

  onMonthChange(): void {
    this.form.controls['lotteryDate'].setValue(null);
    this.updateCalendarStartAt();
  }

  private updateCalendarStartAt(): void {
    const monthId = this.form.value.monthId;
    const financialYearId = this.form.value.financialYearId;

    if (monthId > 0 && financialYearId) {
      const selectedYear = this.financialYearList.find(x => x.key === financialYearId) as any;
      if (selectedYear && selectedYear.startYear && selectedYear.endYear) {
        const year = (monthId >= 4) ? Number(selectedYear.startYear) : Number(selectedYear.endYear);
        this.calendarStartAt = new Date(year, monthId - 1, 1);
        this.calendarMin = new Date(year, monthId - 1, 1);
        this.calendarMax = new Date(year, monthId, 0); // Last day of the selected month
      }
    } else {
      this.calendarMin = null;
      this.calendarMax = null;
    }
  }

  private formatLotteryDate(value: any): string {
    if (!value) {
      return '';
    }

    const date = value.toDate ? value.toDate() : value;

    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    return `${day}-${month}-${date.getFullYear()}`;
  }

  private loadLotteryRunDates(): void {
    const financialYearId = this.form.value.financialYearId || 0;
    const departmentId = this.form.value.departmentId || 0;
    if (!financialYearId || !departmentId) {
      this.lotteryRunDates = [];
      this.form.controls['lotteryDate'].setValue(null);
      return;
    }

    const goId = this.form.value.goId || 0;
    const schemeId = this.form.value.schemeId || 0;
    const componentId = this.form.value.componentId || 0;
    const productId = this.form.value.productId || 0;
    this.lotteryRunDates = [];
    this.form.controls['lotteryDate'].setValue(null);

    this.lotteryService
      .getDashboardList(financialYearId, departmentId, 0, goId, schemeId, componentId, productId, true)
      .subscribe((result: any[]) => {
        this.lotteryRunDates = [];
        (result || []).forEach(row => {
          if (row && row.lotteryDate && this.lotteryRunDates.indexOf(row.lotteryDate) < 0) {
            this.lotteryRunDates.push(row.lotteryDate);
          }
        });
      });
  }

  private regionLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getRegions(financialYearId, true).subscribe(result => {
      this.regionList = result || [];
      this.filterRegionList = this.regionList.slice();

      if (this.filterRegionList.length === 1) {
        this.form.controls['regionId'].setValue(this.filterRegionList[0].key);
        this.onRegionChange();
      }
    });
  }

  onRegionChange(): void {
    this.resetGeoFrom('divisionId');

    const regionId = this.form.value.regionId || 0;
    if (!regionId) {
      return;
    }
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getDivisions(regionId, financialYearId, true).subscribe(result => {
      this.divisionList = result || [];
      this.filterDivisionList = this.divisionList.slice();

      if (this.filterDivisionList.length === 1) {
        this.form.controls['divisionId'].setValue(this.filterDivisionList[0].key);
        this.onDivisionChange();
      }
    });
  }

  onDivisionChange(): void {
    this.resetGeoFrom('subDivisionId');

    const divisionId = this.form.value.divisionId || 0;
    if (!divisionId) {
      return;
    }
    const financialYearId = this.form.value.financialYearId ?? 0;
    this.administrativeMappingService.getSubDivisions(divisionId, financialYearId, true).subscribe(result => {
      this.subDivisionList = result || [];
      this.filterSubDivisionList = this.subDivisionList.slice();

      if (this.filterSubDivisionList.length === 1) {
        this.form.controls['subDivisionId'].setValue(this.filterSubDivisionList[0].key);
        this.onSubDivisionChange();
      }
    });
  }

  onSubDivisionChange(): void {
    this.resetGeoFrom('villageId');

    const subDivisionId = this.form.value.subDivisionId || 0;
    if (!subDivisionId) {
      return;
    }
    this.divisionService.getvillageListbyId(true, subDivisionId).subscribe(result => {
      this.villageList = result || [];
      this.filterVillageList = this.villageList.slice();

      if (this.filterVillageList.length === 1) {
        this.form.controls['villageId'].setValue(this.filterVillageList[0].key);
      }
    });
  }

  onVillageChange(): void {
  }

  onProductChange(): void {
    this.loadLotteryRunDates();
  }

  private resetGeoFrom(controlName: 'regionId' | 'divisionId' | 'subDivisionId' | 'villageId'): void {
    const order = ['regionId', 'divisionId', 'subDivisionId', 'villageId'];
    const startIndex = order.indexOf(controlName);
    order.slice(startIndex).forEach(name => this.form.controls[name].setValue(0));

    if (startIndex <= 1) { this.divisionList = []; this.filterDivisionList = []; }
    if (startIndex <= 2) { this.subDivisionList = []; this.filterSubDivisionList = []; }
    if (startIndex <= 3) { this.villageList = []; this.filterVillageList = []; }
  }

  onDepartmentChange(): void {
    this.clearGoSchemeComponent();
    this.goLookup();
    this.loadLotteryRunDates();
  }

  private clearGoSchemeComponent(): void {
    this.goList = [];
    this.filterGoList = [];
    this.form.controls['goId'].setValue(0);
    this.clearSchemeComponent();
  }

  private clearSchemeComponent(): void {
    this.schemeList = [];
    this.filterSchemeList = [];
    this.form.controls['schemeId'].setValue(0);
    this.clearComponent();
  }

  private clearComponent(): void {
    this.componentList = [];
    this.filterComponentList = [];
    this.form.controls['componentId'].setValue(0);
    this.clearProduct();
  }

  private clearProduct(): void {
    this.productList = [];
    this.filterProductList = [];
    this.form.controls['productId'].setValue(0);
  }

  private goLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    this.paccsbookingService.getGoListbyId(financialYearId, departmentId, true).subscribe(result => {
      this.goList = result || [];
      this.filterGoList = this.goList.slice();

      if (this.filterGoList.length === 1) {
        this.form.controls['goId'].setValue(this.filterGoList[0].key);
        this.onGoChange();
      }
    });
  }

  onGoChange(): void {
    this.clearSchemeComponent();
    this.schemeLookup();
    this.loadLotteryRunDates();
  }

  private schemeLookup(): void {
    const goId = this.form.value.goId ?? 0;
    this.divisionService.getSchemaListbyId(true, goId).subscribe(result => {
      this.schemeList = result || [];
      this.filterSchemeList = this.schemeList.slice();

      if (this.filterSchemeList.length === 1) {
        this.form.controls['schemeId'].setValue(this.filterSchemeList[0].key);
        this.onSchemeChange();
      }
    });
  }

  onSchemeChange(): void {
    this.clearComponent();
    this.componentLookup();
    this.loadLotteryRunDates();
  }

  private componentLookup(): void {
    const goId = this.form.value.goId ?? 0;
    const schemeId = this.form.value.schemeId ?? 0;
    this.divisionService.getComponetListbyId(goId, schemeId, true).subscribe(result => {
      this.componentList = result || [];
      this.filterComponentList = this.componentList.slice();

      if (this.filterComponentList.length === 1) {
        this.form.controls['componentId'].setValue(this.filterComponentList[0].key);
      }
      this.onComponentChange();
    });
  }

  onComponentChange(): void {
    this.clearProduct();
    this.productLookup();
    this.loadLotteryRunDates();
  }

  private productLookup(): void {
    const financialYearId = this.form.value.financialYearId ?? 0;
    const departmentId = this.form.value.departmentId ?? 0;
    const goId = this.form.value.goId ?? 0;
    const schemeId = this.form.value.schemeId ?? 0;
    const componentId = this.form.value.componentId ?? 0;

    if (!goId || !schemeId || !componentId) {
      return;
    }

    this.divisionService.getfilteredProductList(true, financialYearId, departmentId, goId, schemeId, componentId)
      .subscribe(result => {
        this.productList = result || [];
        this.filterProductList = this.productList.slice();
      });
  }

  getDepartments(refresh: boolean): void {
    this.vehicleService.getDepartmentLookupData(refresh).subscribe(result => {
      this.departmentList = result || [];
      this.filterDepartmentList = this.departmentList.slice();

      const smam = this.filterDepartmentList.find(d => d.key === DepartmentType.SMAM);
      if (smam) {
        this.form.controls['departmentId'].setValue(smam.key);
      } else if (this.filterDepartmentList.length === 1) {
        this.form.controls['departmentId'].setValue(this.filterDepartmentList[0].key);
      }

      this.onDepartmentChange();
    });
  }

  private validateFormControl(): void {
    Object.keys(this.form.controls).forEach(field => {
      const control = this.form.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  showReport(): void {
    if (!this.form.valid) {
      this.validateFormControl();
      return;
    }

    const v = this.form.value;
    const nameOf = (list: LookUpResponse[], id: any, fallback: string) =>
      (id ? list.find(x => x.key === id)?.value : '') || fallback;
    const goIdvalue = this.goList.find(item => item.key === v.goId)?.value || 'All';

    // Shared report viewer (same one the other DBT reports use); ReportType 69 picks
    // ReportType.ManufacturerReport server-side via POST /api/report.
    this.tripDialog.open(ShowDailyReportComponent, {
      autoFocus: false,
      disableClose: true,
      width: '95%',
      height: '90%',
      panelClass: 'mat-dialog-report',
      data: {
        financialyearId: v.financialYearId,
        financialyearIdvalue: nameOf(this.filterFinancialYearList, v.financialYearId, ''),
        departmentId: v.departmentId,
        GoId: v.goId || 0,
        goIdvalue,
        schemeId: v.schemeId || 0,
        schemeIdvalue: nameOf(this.filterSchemeList, v.schemeId, 'All'),
        componentId: v.componentId || 0,
        componentIdvalue: nameOf(this.filterComponentList, v.componentId, 'All'),
        ProductId: v.productId || 0,
        DivisionId: v.divisionId || 0,
        SubDivisionId: v.subDivisionId || 0,
        VillageId: v.villageId || 0,
        monthId: v.monthId,
        StartTs: v.monthId > 0 && v.lotteryDate ? moment(v.lotteryDate).format('YYYY-MM-DD') : undefined,
        ReportType: 69,
        downloadType: 4
      }
    });
  }

  clear(): void {
    this.regionList = [];
    this.filterRegionList = [];
    this.divisionList = [];
    this.filterDivisionList = [];
    this.subDivisionList = [];
    this.filterSubDivisionList = [];
    this.villageList = [];
    this.filterVillageList = [];
    this.departmentList = [];
    this.filterDepartmentList = [];
    this.goList = [];
    this.filterGoList = [];
    this.schemeList = [];
    this.filterSchemeList = [];
    this.componentList = [];
    this.filterComponentList = [];
    this.productList = [];
    this.filterProductList = [];
    this.lotteryRunDates = [];
    this.initializeForm();
    this.getFinancialYear(true);
  }
}
